const ALL_PERMISSIONS = [
  'mint_tokens',
  'burn_tokens',
  'verify_assets',
  'asset_nullification',
  'ownership_management',
  'asset_registration',
  'physical_delivery',
  'delivery_verification',
  'warehouse_operations',
  'redemption_processing',
  'valuation_updates',
  'fractional_management',
  'compliance_verification',
  'regulatory_reporting',
  'audit_access',
  'user_management',
  'issuer_management',
  'system_configuration'
];

const TOKENIZER_PERMISSIONS = [
  'mint_tokens',
  'verify_assets',
  'asset_registration',
  'valuation_updates'
];

const LEGAL_PERMISSIONS = ['compliance_verification', 'audit_access'];
const AUDITOR_PERMISSIONS = ['audit_access'];

const safeParseJson = (value, fallback) => {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const toPascalCase = (value = '') =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');

export const makeAssetClassCode = (name = '') =>
  name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || `ASSET_CLASS_${Date.now()}`;

const deriveTicker = (code = '', name = '') => {
  const source = code || name;
  const letters = source.replace(/[^A-Za-z]/g, '').toUpperCase();
  return letters.slice(0, 6) || 'ASSET';
};

const deriveTradingUnit = (sector) => {
  switch (sector) {
    case 'PreciousMetals':
      return 'oz';
    case 'Energy':
    case 'Agriculture':
      return 'unit';
    case 'Music':
    case 'Entertainment':
      return 'right';
    default:
      return 'unit';
  }
};

const deriveIssuerType = (sector) => {
  switch (sector) {
    case 'PreciousMetals':
      return 'specialized_custodian';
    case 'Energy':
    case 'Agriculture':
      return 'asset_originator';
    case 'Technology':
    case 'PatentsAndInnovation':
      return 'institutional';
    default:
      return 'institutional';
  }
};

/** Maps API numeric/string values to C# SectorType names (JsonStringEnumConverter on API). */
export function normalizeSectorType(raw) {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const byNum = {
      1: 'RealEstate',
      2: 'PreciousMetals',
      3: 'Energy',
      4: 'Technology',
      5: 'PatentsAndInnovation',
      6: 'Entertainment',
      7: 'AutomotiveAndVehicles',
      8: 'Collectibles',
      9: 'Agriculture',
      10: 'Music',
      11: 'ArtAndCreativeWorks'
    };
    return byNum[raw] || null;
  }
  const s = String(raw).trim();
  if (/^\d+$/.test(s)) {
    return normalizeSectorType(Number(s));
  }
  const lower = s.toLowerCase().replace(/[\s_-]+/g, '');
  const pascalOk = [
    'RealEstate',
    'PreciousMetals',
    'Energy',
    'Technology',
    'PatentsAndInnovation',
    'Entertainment',
    'AutomotiveAndVehicles',
    'Collectibles',
    'Agriculture',
    'Music',
    'ArtAndCreativeWorks'
  ];
  if (pascalOk.includes(s)) {
    return s;
  }
  if (lower === 'realestate') return 'RealEstate';
  if (lower === 'preciousmetals') return 'PreciousMetals';
  if (lower === 'solar' || lower === 'energy' || lower === 'solarenergy') return 'Energy';
  if (lower === 'technology') return 'Technology';
  if (lower === 'patentsandinnovation' || lower === 'patentsinnovation') return 'PatentsAndInnovation';
  if (lower === 'entertainment') return 'Entertainment';
  if (lower === 'automotiveandvehicles' || lower === 'automotivevehicles') return 'AutomotiveAndVehicles';
  if (lower === 'collectibles') return 'Collectibles';
  if (lower === 'agriculture') return 'Agriculture';
  if (lower === 'music') return 'Music';
  if (lower === 'artandcreativeworks' || lower === 'artcreativeworks') return 'ArtAndCreativeWorks';
  return null;
}

/** Backend AssetStatus (DomainModel.cs) — index equals enum numeric value; slot 0 unused. */
const ASSET_STATUS_NAMES_BY_ENUM_VALUE = [
  '',
  'Draft',
  'SubmittedForLegalReview',
  'UnderLegalReview',
  'ApprovedForTokenization',
  'Rejected',
  'MetadataPinnedToIpfs',
  'MintRequested',
  'MintingInProgress',
  'Minted',
  'VaultLockInProgress',
  'LockedInVault',
  'FractionalizationInProgress',
  'Fractionalized',
  'Burned'
];

/** Coerce API status (numeric enum, string digit, Pascal/camel name) to canonical PascalCase name. */
export function normalizeAssetStatusFromApi(raw) {
  if (raw === null || raw === undefined || raw === '') {
    return '';
  }
  if (typeof raw === 'number' && Number.isFinite(raw) && Number.isInteger(raw)) {
    return ASSET_STATUS_NAMES_BY_ENUM_VALUE[raw] || String(raw);
  }
  const s = String(raw).trim();
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    return ASSET_STATUS_NAMES_BY_ENUM_VALUE[n] || s;
  }
  const compact = s.toLowerCase().replace(/[\s_-]+/g, '');
  const found = ASSET_STATUS_NAMES_BY_ENUM_VALUE.find(
    (name) => name && name.toLowerCase().replace(/[\s_-]+/g, '') === compact
  );
  if (found) return found;
  return s;
}

export const mapSectorToCategory = (sector) => {
  const n = normalizeSectorType(sector);
  switch (n) {
    case 'RealEstate':
      return 'realEstate';
    case 'PreciousMetals':
      return 'preciousMetals';
    case 'Energy':
      return 'energy';
    case 'Technology':
      return 'technology';
    case 'PatentsAndInnovation':
      return 'patentsInnovation';
    case 'Entertainment':
      return 'entertainment';
    case 'AutomotiveAndVehicles':
      return 'automotiveVehicles';
    case 'Collectibles':
      return 'collectibles';
    case 'Agriculture':
      return 'agriculture';
    case 'Music':
      return 'music';
    case 'ArtAndCreativeWorks':
      return 'artCreativeWorks';
    default:
      return 'realEstate';
  }
};

/** Platform tokenization sectors (admin: authorized tokenizers & legal reviewer firms). */
export const TOKENIZER_PLATFORM_SECTORS = [
  {
    id: 'technology',
    name: 'Technology',
    description: 'Patents, hardware, IP rights, software.',
    icon: '💻'
  },
  {
    id: 'energy',
    name: 'Energy',
    description: 'Renewable energy tokens, carbon credits, oil.',
    icon: '⚡'
  },
  {
    id: 'realEstate',
    name: 'Real estate',
    description: 'Residential, commercial, land properties.',
    icon: '🏠'
  },
  {
    id: 'patentsInnovation',
    name: 'Patents & innovation',
    description: 'Registered IPs and R&D results.',
    icon: '🔬'
  },
  {
    id: 'entertainment',
    name: 'Entertainment',
    description: 'Movie rights, royalties, content.',
    icon: '🎬'
  },
  {
    id: 'automotiveVehicles',
    name: 'Automotive & vehicles',
    description: 'Cars, motorcycles, vehicle parts, composable NFTs.',
    icon: '🚗'
  },
  {
    id: 'collectibles',
    name: 'Collectibles',
    description: 'Toys, comics, memorabilia, rare items.',
    icon: '🎴'
  },
  {
    id: 'preciousMetals',
    name: 'Precious metals',
    description: 'Gold, silver, platinum bars and coins.',
    icon: '🥇'
  },
  {
    id: 'agriculture',
    name: 'Agriculture',
    description: 'Crops, farmland, livestock, commodities.',
    icon: '🌾'
  },
  {
    id: 'music',
    name: 'Music',
    description: 'Tracks, royalties, albums, publishing rights.',
    icon: '🎵'
  },
  {
    id: 'artCreativeWorks',
    name: 'Art & creative works',
    description: 'Paintings, digital art, sculptures.',
    icon: '🎨'
  }
];

export const labelTokenizerPlatformCategoryId = (id) =>
  TOKENIZER_PLATFORM_SECTORS.find((s) => s.id === id)?.name || id;

/** Merge legacy allowedCategories with API sectors when opening the tokenizer wizard. */
export function mergeTokenizerFormCategories(issuer) {
  if (!issuer) {
    return [];
  }
  const validIds = new Set(TOKENIZER_PLATFORM_SECTORS.map((s) => s.id));
  const fromCats = (issuer.allowedCategories || []).filter((id) => validIds.has(id));
  const fromSectors = (issuer.sectors || [])
    .map((sec) => normalizeSectorType(sec))
    .filter(Boolean)
    .map((st) => mapSectorToCategory(st))
    .filter((id) => validIds.has(id));
  return [...new Set([...fromCats, ...fromSectors])];
}

export const mapCategoryToSector = (category) => {
  const key = String(category || '')
    .replace(/_/g, '')
    .toLowerCase();

  switch (key) {
    case 'realestate':
      return 'RealEstate';
    case 'preciousmetals':
      return 'PreciousMetals';
    case 'energy':
    case 'solar':
      return 'Energy';
    case 'technology':
      return 'Technology';
    case 'patents':
    case 'patentsinnovation':
      return 'PatentsAndInnovation';
    case 'entertainment':
      return 'Entertainment';
    case 'automotivevehicles':
    case 'automotive':
      return 'AutomotiveAndVehicles';
    case 'collectibles':
      return 'Collectibles';
    case 'agriculture':
      return 'Agriculture';
    case 'music':
      return 'Music';
    case 'artcreativeworks':
    case 'artcreative':
      return 'ArtAndCreativeWorks';
    default:
      return 'RealEstate';
  }
};

/**
 * Sector enums (e.g. RealEstate) the signed-in tokenizer may tokenize, from profile allowedCategories.
 * Empty set means the tokenizer has no sectors assigned. Null means do not restrict (admin / non-tokenizer).
 */
export function allowedSectorEnumsFromProfileCategories(allowedCategoryIds) {
  if (!allowedCategoryIds?.length) {
    return new Set();
  }
  return new Set(allowedCategoryIds.map((id) => mapCategoryToSector(id)));
}

/** Same union as mergeTokenizerFormCategories — use for gating UI when issuer may only expose `sectors`. */
export function allowedSectorEnumsFromIssuerProfile(issuer) {
  const ids = mergeTokenizerFormCategories(issuer);
  if (!ids.length) {
    return new Set();
  }
  return new Set(ids.map((id) => mapCategoryToSector(id)));
}

/** @param {Set<string>|null} allowedSectorEnums — null = unrestricted */
export function isUiCategoryAllowedForTokenizer(categoryId, allowedSectorEnums) {
  if (allowedSectorEnums == null) return true;
  return allowedSectorEnums.has(mapCategoryToSector(categoryId));
}

/** @param {Set<string>|null} allowedSectorEnums — null = unrestricted */
export function isAssetClassSectorAllowedForTokenizer(assetClass, allowedSectorEnums) {
  if (allowedSectorEnums == null) return true;
  const s = normalizeSectorType(assetClass?.sector);
  return Boolean(s && allowedSectorEnums.has(s));
}

/**
 * Pick an authorized tokenizer GUID for POST /api/assets.
 * Backend requires: tokenizer approved + tokenizer.Sectors includes asset sector.
 *
 * When the signed-in user is a tokenizer operator, `issuers` may be empty or stale; if their profile
 * already lists the sector (same source as category gating), return their tokenizer id.
 */
export const resolveAuthorizedTokenizerIdForSector = (sector, issuers = [], currentIssuer = null) => {
  const normalizedSector = normalizeSectorType(sector) || 'RealEstate';
  const list = Array.isArray(issuers) ? issuers : [];

  const issuerCoversSector = (issuer) => {
    const issuerSectors = (issuer?.sectors || [])
      .map((s) => normalizeSectorType(s))
      .filter(Boolean);
    return issuerSectors.includes(normalizedSector);
  };

  const matchesSector = (issuer) => issuer?.status === 'active' && issuerCoversSector(issuer);

  if (currentIssuer?.tokenizerId) {
    const self = list.find((i) => i.id === currentIssuer.tokenizerId || i.tokenizerId === currentIssuer.tokenizerId);
    if (self && matchesSector(self)) {
      return currentIssuer.tokenizerId;
    }

    const profileSectors = allowedSectorEnumsFromIssuerProfile(currentIssuer);
    const userStatus = String(currentIssuer?.status || '').toLowerCase();
    const userActive = userStatus === 'active' || userStatus === 'approved';
    if (userActive && profileSectors.size > 0 && profileSectors.has(normalizedSector)) {
      return currentIssuer.tokenizerId;
    }
  }

  const fallback = list.find((i) => matchesSector(i));
  return fallback?.tokenizerId || fallback?.id || null;
};

export const mapStatusToIssuerStatus = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'approved':
    case 'active':
      return 'active';
    case 'suspended':
      return 'suspended';
    case 'pending':
    case 'pending_verification':
    default:
      return 'pending_verification';
  }
};

export const formatSectorLabel = (sector) => {
  const n = normalizeSectorType(sector);
  const fromUi = TOKENIZER_PLATFORM_SECTORS.find((x) => mapCategoryToSector(x.id) === n);
  if (fromUi) {
    return fromUi.name;
  }
  switch (n) {
    case 'RealEstate':
      return 'Real estate';
    case 'PreciousMetals':
      return 'Precious metals';
    case 'Energy':
      return 'Energy';
    default:
      return sector ? String(sector) : '—';
  }
};

/** Build API body for POST/PUT legal-review-firms (sectors as C# enum names). */
export const categoryIdsToSectorEnums = (categoryIds = []) =>
  [...new Set((categoryIds || []).map((id) => mapCategoryToSector(id)).filter(Boolean))];

export const mapLegalFirmFromApi = (firm) => {
  const sectors = (firm?.sectors || firm?.Sectors || []).map(extractSectorName).filter(Boolean);
  return {
    ...firm,
    sectors,
    sectorCategoryIds: [...new Set(sectors.map(mapSectorToCategory))]
  };
};

const mapUserStatusToIssuerStatus = (status) =>
  status === 'Suspended' ? 'suspended' : 'active';

const derivePermissionsFromRoles = (roles = []) => {
  const normalizedRoles = roles.map((role) => role.toLowerCase());

  if (normalizedRoles.includes('admin')) {
    return ALL_PERMISSIONS;
  }

  const permissions = [];

  if (normalizedRoles.includes('authorizedtokenizer')) {
    permissions.push(...TOKENIZER_PERMISSIONS);
  }

  if (normalizedRoles.includes('legalreviewer')) {
    permissions.push(...LEGAL_PERMISSIONS);
  }

  if (normalizedRoles.includes('auditor')) {
    permissions.push(...AUDITOR_PERMISSIONS);
  }

  return [...new Set(permissions)];
};

const derivePermissionsFromSectors = (status, sectors = []) => {
  if (status !== 'Approved') {
    return [];
  }

  return [...new Set(TOKENIZER_PERMISSIONS)];
};

const extractSectorName = (entry) => {
  const raw = typeof entry === 'string' ? entry : entry?.sector ?? entry?.Sector;
  return normalizeSectorType(raw);
};

export const mapTokenizerToIssuer = (tokenizer) => {
  const sectors = (tokenizer?.sectors || tokenizer?.Sectors || []).map(extractSectorName).filter(Boolean);
  const primarySector = sectors[0] || 'RealEstate';

  return {
    id: tokenizer.id,
    tokenizerId: tokenizer.id,
    name: tokenizer.organizationName || 'Unnamed tokenizer',
    email: tokenizer.email || '',
    type: deriveIssuerType(primarySector),
    description: tokenizer.description || '',
    website: tokenizer.website || '',
    contactPerson: tokenizer.contactPerson || '',
    phone: tokenizer.phone || '',
    jurisdictions: [],
    allowedCategories: sectors.map(mapSectorToCategory),
    allowedRegions: ['global'],
    allowedLocations: [],
    permissions: derivePermissionsFromSectors(tokenizer.status, sectors),
    complianceLevel: 'backend',
    insuranceCoverage: '',
    bondAmount: '',
    status: mapStatusToIssuerStatus(tokenizer.status),
    verificationLevel: tokenizer.status === 'Approved' ? 'approved' : 'pending',
    totalIssuances: tokenizer.assets?.length || 0,
    totalVolume: 0,
    complianceScore: tokenizer.status === 'Approved' ? 100 : 50,
    registrationNumber: tokenizer.registrationNumber || '',
    walletAddress: tokenizer.walletAddress ?? tokenizer.WalletAddress ?? '',
    sectors,
    createdAt: tokenizer.createdAtUtc || tokenizer.createdAt || new Date().toISOString(),
    lastUpdated: tokenizer.updatedAtUtc || tokenizer.updatedAt || tokenizer.createdAtUtc || new Date().toISOString()
  };
};

export const mapProfileToCurrentIssuer = (profile, matchingTokenizer) => {
  const roles = profile?.roles ?? profile?.Roles ?? [];
  const permissions = derivePermissionsFromRoles(roles);
  const tokenizerIssuer = matchingTokenizer ? mapTokenizerToIssuer(matchingTokenizer) : null;
  const authorizedTokenizerId =
    profile?.authorizedTokenizerId ?? profile?.AuthorizedTokenizerId ?? null;
  const legalReviewFirmId = profile?.legalReviewFirmId ?? profile?.LegalReviewFirmId ?? null;

  return {
    id: profile.userId ?? profile.UserId,
    userId: profile.userId ?? profile.UserId,
    tokenizerId: tokenizerIssuer?.tokenizerId || null,
    authorizedTokenizerId,
    legalReviewFirmId,
    name: profile.fullName || profile.FullName || profile.organizationName || profile.OrganizationName || profile.email || profile.Email,
    email: profile.email ?? profile.Email,
    organizationName: profile.organizationName ?? profile.OrganizationName,
    roles,
    status: mapUserStatusToIssuerStatus(profile.status ?? profile.Status),
    permissions,
    allowedCategories:
      tokenizerIssuer?.allowedCategories ||
      (roles.some((r) => String(r).toLowerCase() === 'admin')
        ? TOKENIZER_PLATFORM_SECTORS.map((s) => s.id)
        : []),
    sectors: tokenizerIssuer?.sectors ?? [],
    allowedRegions: tokenizerIssuer?.allowedRegions || ['global'],
    allowedLocations: tokenizerIssuer?.allowedLocations || [],
    stats: {
      totalIssuances: 0,
      totalVolume: 0,
      pendingVerifications: 0,
      activeDeliveries: 0,
      totalBurned: 0
    },
    walletAddress: String(
      tokenizerIssuer?.walletAddress ||
        (profile.walletAddress ?? profile.WalletAddress ?? '')
    ).trim()
  };
};

export const toAuthorizedTokenizerRequest = (formData) => {
  const sectors = [...new Set((formData.allowedCategories || []).map(mapCategoryToSector))];

  return {
    organizationName: formData.name?.trim() || 'Unnamed tokenizer',
    registrationNumber:
      formData.registrationNumber?.trim() ||
      formData.phone?.trim() ||
      `TK-${Date.now()}`,
    walletAddress: formData.walletAddress?.trim() || '0x0000000000000000000000000000000000000000',
    sectors
  };
};

export const toAuthorizedTokenizerUpdateRequest = (issuer, updates = {}) => {
  const merged = { ...issuer, ...updates };
  return toAuthorizedTokenizerRequest(merged);
};

/** @param {unknown} raw */
export function normalizeAssetClassTokenStandard(raw) {
  if (raw === null || raw === undefined || raw === '') return 'ERC721';
  if (typeof raw === 'number') {
    if (raw === 2) return 'ERC1155';
    if (raw === 3) return 'ERC20';
    return 'ERC721';
  }
  const s = String(raw).trim();
  const c = s.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  if (c === 'CBC1155') return 'ERC1155';
  if (c === 'CBC721') return 'ERC721';
  if (c === 'CBC20') return 'ERC20';
  if (c === 'ERC1155' || s === '2') return 'ERC1155';
  if (c === 'ERC20' || s === '3') return 'ERC20';
  if (c === 'ERC721' || s === '1') return 'ERC721';
  if (/^ERC-?1155$/i.test(s)) return 'ERC1155';
  if (/^ERC-?20$/i.test(s)) return 'ERC20';
  return 'ERC721';
}

/** True when mapped asset has an asset class whose token standard resolves to ERC-721 (vault + fractional flow). */
export function isMappedAssetClassErc721(mapped) {
  const ts = mapped?.tokenStandard;
  if (ts === undefined || ts === null || String(ts).trim() === '') return false;
  return normalizeAssetClassTokenStandard(ts) === 'ERC721';
}

const normalizeStandardSchema = (parsed) => {
  const raw = parsed && typeof parsed === 'object' ? parsed : {};
  const required = Array.isArray(raw.required) ? raw.required : [];
  const specsRaw = raw.specifications;
  const specifications =
    specsRaw && typeof specsRaw === 'object' && !Array.isArray(specsRaw) ? specsRaw : {};
  return { ...raw, required, specifications };
};

export const mapAssetClassToFrontend = (assetClass) => {
  const standardSchema = normalizeStandardSchema(
    safeParseJson(assetClass.metadataSchemaJson, {
      required: [],
      specifications: {}
    })
  );
  const sector = normalizeSectorType(assetClass.sector) || 'RealEstate';
  const tokenStandard = normalizeAssetClassTokenStandard(
    assetClass.tokenStandard ?? assetClass.TokenStandard ?? 'ERC721'
  );

  const statusVal = assetClass.status;
  const isActive =
    statusVal === 'Active' ||
    statusVal === 2 ||
    String(statusVal).toLowerCase() === 'active';

  const classType =
    tokenStandard === 'ERC1155'
      ? 'fractionalAsset'
      : tokenStandard === 'ERC20'
        ? 'fungibleCommodity'
        : 'standardizedSeries';

  return {
    id: assetClass.id ?? assetClass.Id,
    code: assetClass.code,
    name: assetClass.name,
    isPlatformStandard: Boolean(assetClass.isPlatformStandard ?? assetClass.IsPlatformStandard),
    contractAccessMode:
      assetClass.contractAccessMode ?? assetClass.ContractAccessMode ?? 'Unspecified',
    contractDeployedByAuthorizedTokenizerId:
      assetClass.contractDeployedByAuthorizedTokenizerId ??
      assetClass.ContractDeployedByAuthorizedTokenizerId ??
      null,
    createdByAuthorizedTokenizerId:
      assetClass.createdByAuthorizedTokenizerId ??
      assetClass.CreatedByAuthorizedTokenizerId ??
      null,
    category: mapSectorToCategory(sector),
    sector,
    tokenStandard,
    classType,
    marketSymbol: deriveTicker(assetClass.code, assetClass.name),
    tradingUnit: deriveTradingUnit(sector),
    description: assetClass.description || assetClass.name,
    standardSchema,
    contractAddress: assetClass.contractAddress || '',
    contractDeployedAtUtc:
      assetClass.contractDeployedAtUtc ?? assetClass.ContractDeployedAtUtc ?? null,
    custodyVaultContractAddress: String(
      assetClass.custodyVaultContractAddress ?? assetClass.CustodyVaultContractAddress ?? ''
    ).trim(),
    custodyVaultLockFunctionName: String(
      assetClass.custodyVaultLockFunctionName ??
        assetClass.CustodyVaultLockFunctionName ??
        'safeTransferFrom'
    ).trim(),
    fractionTokenContractAddress: String(
      assetClass.fractionTokenContractAddress ?? assetClass.FractionTokenContractAddress ?? ''
    ).trim(),
    fractionTokenMintFunctionName: String(
      assetClass.fractionTokenMintFunctionName ?? assetClass.FractionTokenMintFunctionName ?? 'mint'
    ).trim(),
    createdAt: assetClass.createdAtUtc || new Date().toISOString(),
    totalBatches: assetClass.assets?.length || 0,
    totalSupply: assetClass.assets?.length || 0,
    isActive,
    status: assetClass.status,
    batchable: tokenStandard === 'ERC1155' || tokenStandard === 'ERC20',
    tradingMode:
      tokenStandard === 'ERC1155' ? 'fractional' : tokenStandard === 'ERC20' ? 'fungible' : 'class',
    batchIds: [],
    lastBatchDate: null
  };
};

export const toAssetClassRequest = (classData, options = {}) => {
  const sector = mapCategoryToSector(classData.category);
  const explicit = classData.tokenStandard && String(classData.tokenStandard).trim();
  const tokenStandard = explicit
    ? normalizeAssetClassTokenStandard(classData.tokenStandard)
    : classData.classType === 'fractionalAsset'
      ? 'ERC1155'
      : classData.classType === 'fungibleCommodity'
        ? 'ERC20'
        : 'ERC721';

  return {
    code: makeAssetClassCode(classData.name),
    name: classData.name,
    sector,
    tokenStandard,
    isCrowdfundingAllowed: false,
    metadataSchemaJson: JSON.stringify(classData.standardSchema || { required: [], specifications: {} }),
    isPlatformStandard: Boolean(options.isPlatformStandard)
  };
};

export function mapAssetClassTemplateDtoToFrontend(row) {
  if (!row) return null;
  const sector = normalizeSectorType(row.sector ?? row.Sector);
  const standardSchema = normalizeStandardSchema(
    safeParseJson(row.metadataSchemaJson ?? row.MetadataSchemaJson, {
      required: [],
      specifications: {}
    })
  );
  const compRaw = row.composabilityJson ?? row.ComposabilityJson;
  const composability = compRaw ? safeParseJson(compRaw, undefined) : undefined;
  const ts = row.tokenStandard ?? row.TokenStandard;
  const tokenStandard = normalizeAssetClassTokenStandard(ts ?? 'ERC721');
  const t = {
    id: row.templateKey ?? row.TemplateKey,
    templateApiId: row.id ?? row.Id,
    name: row.name ?? row.Name,
    category: mapSectorToCategory(sector),
    sector,
    classType: row.classType ?? row.ClassType ?? 'standardizedSeries',
    tokenStandard,
    standardSchema,
    tradingUnit: row.tradingUnit ?? row.TradingUnit ?? '',
    marketSymbol: row.marketSymbol ?? row.MarketSymbol ?? '',
    displayOrder: row.displayOrder ?? row.DisplayOrder ?? 0,
    isActive: row.isActive ?? row.IsActive ?? true
  };
  if (composability) {
    t.composability = composability;
  }
  return t;
}

export const toAssetClassUpdateRequest = (assetClass, updates = {}) => {
  const merged = { ...assetClass, ...updates };

  return {
    code: merged.code || makeAssetClassCode(merged.name),
    name: merged.name,
    sector: merged.sector || mapCategoryToSector(merged.category),
    tokenStandard:
      merged.tokenStandard ||
      (merged.classType === 'fractionalAsset'
        ? 'ERC1155'
        : merged.classType === 'fungibleCommodity'
          ? 'ERC20'
          : 'ERC721'),
    metadataSchemaJson: JSON.stringify(merged.standardSchema || { required: [], specifications: {} })
  };
};

const buildAssetNumber = (title = '') =>
  `${makeAssetClassCode(title).slice(0, 8)}-${Date.now()}`;

const deriveCountry = (location = '') => {
  const parts = String(location)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  return parts[parts.length - 1] || 'Unknown';
};

export const buildAssetMetadata = (assetData) => {
  const metadata = {
    ...(assetData.metadata || {}),
    description: assetData.description,
    valuationUSD: Number(assetData.valuationUSD || 0),
    location: assetData.location || '',
    country: assetData.country || deriveCountry(assetData.location || ''),
    ownerAddress: assetData.ownerAddress || '',
    custodian: assetData.custodian || '',
    assetType: assetData.assetType || '',
    tokenType: assetData.tokenType || ''
  };

  const extraEntries = Object.entries(assetData).filter(
    ([key, value]) =>
      ![
        'assetClassId',
        'name',
        'description',
        'valuationUSD',
        'ownerAddress',
        'custodian',
        'location',
        'country',
        'documents',
        'metadata',
        'category',
        'isAssetClassMode',
        'assetClassInfo'
      ].includes(key) &&
      value !== undefined &&
      value !== null &&
      value !== ''
  );

  extraEntries.forEach(([key, value]) => {
    metadata[key] = value;
  });

  return metadata;
};

export const resolveAssetClassForAsset = (assetData, assetClasses = []) => {
  if (assetData.assetClassId) {
    return assetClasses.find((assetClass) => assetClass.id === assetData.assetClassId) || null;
  }

  const sector = mapCategoryToSector(assetData.category);
  return (
    assetClasses.find(
      (assetClass) => normalizeSectorType(assetClass.sector) === sector && assetClass.isActive
    ) ||
    assetClasses.find((assetClass) => normalizeSectorType(assetClass.sector) === sector) ||
    null
  );
};

export const toAssetCreateRequest = (assetData, options = {}) => {
  const assetClass = resolveAssetClassForAsset(assetData, options.assetClasses);

  if (!assetClass) {
    throw new Error('Please create or select an asset class before creating an asset.');
  }

  if (!options.authorizedTokenizerId) {
    const sectorForMsg =
      normalizeSectorType(assetClass.sector) || mapCategoryToSector(assetData.category);
    const sectorLabel = formatSectorLabel(sectorForMsg);
    throw new Error(
      `No tokenizer for this sector. The platform requires an approved authorized tokenizer whose sectors include ${sectorLabel}. ` +
        'Go to Authorized tokenizers (admin) or sign in with a tokenizer operator account, then try again.'
    );
  }

  const sectorForApi = normalizeSectorType(assetClass.sector) || mapCategoryToSector(assetData.category);

  return {
    assetClassId: assetClass.id,
    authorizedTokenizerId: options.authorizedTokenizerId,
    assetNumber: assetData.assetNumber || buildAssetNumber(assetData.name),
    title: assetData.name,
    sector: sectorForApi,
    currentOwnerName: assetData.custodian || assetData.currentOwnerName || 'Unspecified owner',
    currentOwnerIdentifier:
      assetData.ownerAddress || assetData.currentOwnerIdentifier || 'OWNER_NOT_PROVIDED',
    country: assetData.country || deriveCountry(assetData.location),
    metadataDraftJson: JSON.stringify(buildAssetMetadata(assetData))
  };
};

export const mapAssetToFrontend = (asset) => {
  const metadata = safeParseJson(asset.metadataDraftJson, {});
  const sectorNorm = normalizeSectorType(asset.sector) || 'RealEstate';
  const status = normalizeAssetStatusFromApi(asset.status ?? asset.Status);
  const ac = asset.assetClass ?? asset.AssetClass ?? null;
  const contractRaw = ac?.contractAddress ?? ac?.ContractAddress ?? '';
  const contractAddress = String(contractRaw || '').trim();
  const tsRaw = ac?.tokenStandard ?? ac?.TokenStandard;
  const tokenStandard =
    tsRaw !== undefined && tsRaw !== null && tsRaw !== ''
      ? normalizeAssetClassTokenStandard(tsRaw)
      : '';
  const acNameRaw = ac?.name ?? ac?.Name;
  const acCodeRaw = ac?.code ?? ac?.Code ?? '';
  const assetClassName =
    acNameRaw !== undefined && acNameRaw !== null && String(acNameRaw).trim() !== ''
      ? String(acNameRaw).trim()
      : null;
  const suggestedFractionTokenName =
    assetClassName != null && String(assetClassName).trim() !== ''
      ? `${String(assetClassName).trim()} fractional shares`
      : 'Asset fractional shares';
  const suggestedFractionTokenSymbol = (() => {
    const c = String(acCodeRaw || '').trim();
    const n = String(acNameRaw ?? '').trim();
    const letters = deriveTicker(c, n)
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 11);
    return letters || 'FRAC';
  })();
  const cvAddr = ac?.custodyVaultContractAddress ?? ac?.CustodyVaultContractAddress;
  const custodyVaultContractAddress =
    cvAddr !== undefined && cvAddr !== null && String(cvAddr).trim() !== '' ? String(cvAddr).trim() : '';
  const cvFn = ac?.custodyVaultLockFunctionName ?? ac?.CustodyVaultLockFunctionName;
  const custodyVaultLockFunctionName =
    cvFn !== undefined && cvFn !== null && String(cvFn).trim() !== ''
      ? String(cvFn).trim()
      : 'safeTransferFrom';
  const ftAddr = ac?.fractionTokenContractAddress ?? ac?.FractionTokenContractAddress;
  const fractionTokenContractAddress =
    ftAddr !== undefined && ftAddr !== null && String(ftAddr).trim() !== '' ? String(ftAddr).trim() : '';
  const ftFn = ac?.fractionTokenMintFunctionName ?? ac?.FractionTokenMintFunctionName;
  const fractionTokenMintFunctionName =
    ftFn !== undefined && ftFn !== null && String(ftFn).trim() !== '' ? String(ftFn).trim() : 'mint';

  return {
    id: asset.id,
    assetClassId: asset.assetClassId,
    assetClassName,
    authorizedTokenizerId: asset.authorizedTokenizerId,
    assetNumber: asset.assetNumber,
    name: asset.title,
    title: asset.title,
    category: mapSectorToCategory(sectorNorm),
    sector: sectorNorm,
    description: metadata.description || '',
    valuationUSD: Number(metadata.valuationUSD || 0),
    ownerAddress: metadata.ownerAddress || asset.currentOwnerIdentifier || '',
    custodian: metadata.custodian || asset.currentOwnerName || '',
    location: metadata.location || asset.country || '',
    country: asset.country || metadata.country || '',
    metadata,
    documents: asset.documents || [],
    status,
    verificationScore: status === 'ApprovedForTokenization' ? 100 : 0,
    createdAt: asset.createdAtUtc || new Date().toISOString(),
    tokenizationId: null,
    legalReviews: asset.legalReviews || [],
    metadataIpfsCid: asset.metadataIpfsCid || null,
    contractAddress,
    tokenStandard,
    custodyVaultContractAddress,
    custodyVaultLockFunctionName,
    fractionTokenContractAddress,
    fractionTokenMintFunctionName,
    suggestedFractionTokenName,
    suggestedFractionTokenSymbol
  };
};

export const toAssetUpdateRequest = (asset, updates = {}) => {
  const merged = { ...asset, ...updates };
  const metadata = {
    ...(asset.metadata || {}),
    ...(updates.metadata || {}),
    description: merged.description,
    valuationUSD: Number(merged.valuationUSD || 0),
    location: merged.location || '',
    ownerAddress: merged.ownerAddress || '',
    custodian: merged.custodian || ''
  };

  return {
    assetNumber: merged.assetNumber || buildAssetNumber(merged.name),
    title: merged.name || merged.title,
    currentOwnerName: merged.custodian || merged.currentOwnerName || 'Unspecified owner',
    currentOwnerIdentifier:
      merged.ownerAddress || merged.currentOwnerIdentifier || 'OWNER_NOT_PROVIDED',
    country: merged.country || deriveCountry(merged.location),
    metadataDraftJson: JSON.stringify(metadata)
  };
};

export const mapBlockchainTransaction = (transaction) => {
  const rawIds = transaction.nftTokenIds ?? transaction.NftTokenIds;
  const nftTokenIds = Array.isArray(rawIds) ? rawIds.map((id) => String(id)) : [];
  return {
    id: transaction.transactionHash,
    hash: transaction.transactionHash,
    txHash: transaction.transactionHash,
    status: (transaction.status || 'pending').toLowerCase(),
    blockNumber: transaction.blockNumber,
    contractAddress: transaction.contractAddress || '',
    from: transaction.from || '',
    to: transaction.to || '',
    value: transaction.value || '0',
    nonce: transaction.nonce,
    input: transaction.input || '',
    nftTokenIds,
    timestamp: new Date().toISOString()
  };
};

export const formatEnumLabel = (value = '') => toPascalCase(String(value));
